/**
 * Table widget (P3-S2 Doc 42).
 */
import type { WidgetSpec } from "@/api/dashboards";

interface ColumnSpec {
  key: string;
  label: string;
  format?: string;
}

interface Props {
  widget: WidgetSpec;
  data: Record<string, unknown> | null;
  loading: boolean;
}

function formatCell(value: unknown, format: string | undefined): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (format === "usd") return `$${value.toFixed(value < 1 ? 4 : 2)}`;
    if (format === "tokens") {
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
      return String(value);
    }
    if (format === "number" || !format) return value.toLocaleString("vi-VN");
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "—";
  }
  return String(value);
}

export function TableWidget({ widget, data, loading }: Props): JSX.Element {
  const columns = (widget.options.columns as ColumnSpec[] | undefined) ?? [];
  const items = Array.isArray(data?.items) ? (data!.items as Record<string, unknown>[]) : [];

  return (
    <div className="h-full flex flex-col">
      <div className="text-xs font-semibold mb-1">{widget.title}</div>
      {widget.description ? (
        <div className="text-[10px] text-muted-foreground mb-1 line-clamp-1">
          {widget.description}
        </div>
      ) : null}
      <div className="flex-1 min-h-0 overflow-auto border rounded">
        {loading ? (
          <div className="p-3 space-y-1">
            <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
            <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
            <div className="h-4 bg-muted animate-pulse rounded w-4/5" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground italic h-full flex items-center justify-center p-4">
            (no data)
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/50">
              <tr>
                {columns.length > 0 ? (
                  columns.map((c) => (
                    <th
                      key={c.key}
                      className="px-2 py-1.5 text-left font-medium"
                    >
                      {c.label}
                    </th>
                  ))
                ) : (
                  <th className="px-2 py-1.5 text-left font-medium">Item</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 100).map((row, i) => (
                <tr key={i} className="border-t hover:bg-muted/30">
                  {columns.length > 0 ? (
                    columns.map((c) => (
                      <td key={c.key} className="px-2 py-1 align-top">
                        {formatCell(row[c.key], c.format)}
                      </td>
                    ))
                  ) : (
                    <td className="px-2 py-1 font-mono text-[10px]">
                      {JSON.stringify(row).slice(0, 200)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
